import * as React from 'react'
import ReactDatePicker, {
  DatePickerProps as ReactDatePickerProps,
  ReactDatePickerCustomHeaderProps,
  registerLocale,
} from 'react-datepicker'
import addDays from 'date-fns/addDays'
import eachYearOfInterval from 'date-fns/eachYearOfInterval'
import getYear from 'date-fns/getYear'
import getMonth from 'date-fns/getMonth'
import eachMonthOfInterval from 'date-fns/eachMonthOfInterval'
import { css, styled } from 'next-yak'

import { DatePickerField } from '../fields/DatePickerField'
import { Dropdown } from '../select/Dropdown'
import { Option } from '../select/Option'

import * as bp from '../../../constants/breakpoints'
import * as colors from '../../../constants/colors'
import * as fonts from '../../../constants/fonts'
import * as glyphs from '../../../icons/glyphs'
import * as S from '../../../styles'
import 'react-datepicker/dist/react-datepicker.css'

type Translations = {
  monthsList: string[]
}

export type DatePickerProps = Omit<ReactDatePickerProps, 'selectsRange' | 'onChange'> & {
  error?: React.ReactNode
  initialDate?: Date
  locale: Locale
  onChange?: (dates: Date | Date[]) => void
  placeholder?: { date?: string; time?: string }
  selectsRange?: boolean
  setDateRange?: React.Dispatch<React.SetStateAction<[Date | undefined, Date | undefined]>>
  translations: Translations
  yearInterval?: number
  style?: React.CSSProperties
}

type CustomInputProps = {
  onClick?: (event: React.MouseEvent<HTMLInputElement>) => void
  value?: string
}

type CustomTimeInputProps = {
  onChange?: (value: string) => void
  value?: string
}

type ControllerDatePickerProps = Omit<ReactDatePickerProps, 'selectsRange' | 'onChange' | 'startDate'> & {
  initialDate?: Date
  locale: Locale
  error?: React.ReactNode
  onChange?: (dates: Date | Date[]) => void
  placeholder?: { date?: string; time?: string }
  selectsRange?: boolean
  setDateRange: React.Dispatch<React.SetStateAction<[Date | undefined, Date | undefined]>>
  startDate?: Date
  translations: Translations
  yearInterval?: number
  style?: React.CSSProperties
}

type HeaderProps = ReactDatePickerCustomHeaderProps & {
  locale: Locale
  maxDate: ReactDatePickerProps['maxDate']
  minDate: ReactDatePickerProps['minDate']
  monthsShown?: number
  showMonthYearDropdown?: boolean
  showMonthYearPicker?: boolean
  showQuarterYearPicker?: boolean
  translations: Translations
  yearInterval: number
}

type SelectedMonthYear = {
  disabled: boolean
  handleFocus: () => void
  handleToggle: () => void
  label: string
  value: {
    monthIndex: number
    year: number
  }
}[]

type Selected = {
  disabled: boolean
  handleFocus: () => void
  handleToggle: () => void
  label: string
  value: string
}[]

function DatePickerComponent(props: DatePickerProps): React.ReactElement<DatePickerProps> {
  verifyPropsCompatibility(props)

  if (props.startDate && (props.selectsRange ? props.endDate : true) && props.setDateRange) {
    return <ControlledDatePicker {...props} startDate={props.startDate} setDateRange={props.setDateRange} />
  } else {
    return <UnControlledDatePicker {...props} />
  }
}

function ControlledDatePicker({
  className,
  style,
  endDate,
  error,
  locale,
  onChange,
  setDateRange,
  showMonthYearPicker,
  showFullMonthYearPicker = showMonthYearPicker,
  showTimeInput = false,
  showTimeSelectOnly = false,
  startDate,
  translations,
  yearInterval = 5,
  ...reactDatePickerProps
}: ControllerDatePickerProps): React.ReactElement<ControllerDatePickerProps> {
  registerLocale(locale.code ?? 'en', locale)

  // TODO: change this input as a button
  const CustomInput = ({ value, onClick }: CustomInputProps) => (
    <DatePickerField
      onBlur={reactDatePickerProps.onBlur}
      onClick={onClick}
      value={value}
      error={error}
      placeholder={
        reactDatePickerProps.placeholder?.date
          ? reactDatePickerProps.placeholder.date
          : reactDatePickerProps.initialDate
            ? reactDatePickerProps.initialDate.toLocaleDateString()
            : new Date().toLocaleDateString()
      }
      showTimeSelectOnly={showTimeSelectOnly}
      readOnly
    />
  )

  const CustomTimeInput = ({ value, onChange }: CustomTimeInputProps) => (
    <S.input.Input
      onChange={(event) => onChange && onChange(event.target.value)}
      value={value}
      placeholder={
        reactDatePickerProps.placeholder?.time
          ? reactDatePickerProps.placeholder.time
          : reactDatePickerProps.initialDate
            ? reactDatePickerProps.initialDate.toLocaleDateString()
            : new Date().toLocaleDateString()
      }
      type="time"
    />
  )

  return (
    <div className={`${className} datepicker-wrapper`} style={style}>
      {/*@ts-expect-error: react date picker is configured both for single and multiple selection, but types expect either single or multiple */}
      <ReactDatePicker
        customInput={<CustomInput />}
        customTimeInput={<CustomTimeInput />}
        endDate={reactDatePickerProps.selectsRange ? endDate : undefined}
        onChange={
          (reactDatePickerProps.selectsRange
            ? (dates: [Date, Date]) => onChangeRange(dates)
            : (date: Date) => onChangeDate(date)) as any
        }
        openToDate={startDate}
        renderCustomHeader={(headerProps: any) => (
          <Header
            locale={locale}
            maxDate={reactDatePickerProps.maxDate}
            minDate={reactDatePickerProps.minDate}
            monthsShown={reactDatePickerProps.monthsShown}
            showMonthYearPicker={showMonthYearPicker}
            showQuarterYearPicker={reactDatePickerProps.showQuarterYearPicker}
            showMonthYearDropdown={reactDatePickerProps.showMonthYearDropdown}
            translations={translations}
            yearInterval={yearInterval}
            {...headerProps}
          />
        )}
        selected={startDate}
        showFullMonthYearPicker={showFullMonthYearPicker}
        showMonthYearPicker={showMonthYearPicker}
        showTimeInput={showTimeInput}
        showTimeSelectOnly={showTimeSelectOnly}
        startDate={startDate}
        locale={locale}
        {...reactDatePickerProps}
      >
        {reactDatePickerProps.children}
      </ReactDatePicker>
    </div>
  )

  function onChangeRange(dates: [Date, Date]) {
    setDateRange(dates)
    onChange && onChange(dates)
  }

  function onChangeDate(date: Date) {
    setDateRange([date, undefined])
    onChange && onChange(date)
  }
}

function UnControlledDatePicker(props: DatePickerProps): React.ReactElement<DatePickerProps> {
  const [[startDate, endDate], setDateRange] = React.useState<[Date | undefined, Date | undefined]>([
    props.initialDate,
    undefined,
  ])

  return <ControlledDatePicker {...props} startDate={startDate} endDate={endDate} setDateRange={setDateRange} />
}

function Header({
  changeMonth,
  changeYear,
  customHeaderCount,
  date,
  decreaseMonth,
  increaseMonth,
  locale,
  maxDate,
  minDate,
  monthDate,
  monthsShown,
  nextMonthButtonDisabled,
  prevMonthButtonDisabled,
  showMonthYearDropdown,
  showMonthYearPicker,
  showQuarterYearPicker,
  translations,
  yearInterval,
}: HeaderProps) {
  if (!monthDate) {
    return <></>
  }

  const months = translations.monthsList
  const intervalMonths = eachMonthOfInterval({
    start: minDate ?? new Date(),
    end: maxDate ?? addDays(new Date(), 90),
  })
  const monthByYearOptions: { label: string; value: { monthIndex: number; year: number } }[] = intervalMonths.map(
    (date) => {
      return {
        label: `${months[date.getMonth()]} ${date.getFullYear()}`,
        value: {
          monthIndex: date.getMonth(),
          year: date.getFullYear(),
        },
      }
    },
  )

  const endDateInterval = new Date()
  endDateInterval.setFullYear(endDateInterval.getFullYear() + yearInterval)
  const dateYears: Date[] = eachYearOfInterval({ start: new Date(), end: endDateInterval })
  const alwaysShowControls = monthsShown === undefined || monthsShown < 2

  return (
    <S.datePicker.Header>
      {(alwaysShowControls ? true : customHeaderCount % 2 === 0) && !showQuarterYearPicker && (
        <S.datePicker.PrevButton type="button" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
          <glyphs.stroke.CaretDown size={18} />
        </S.datePicker.PrevButton>
      )}

      <S.datePicker.HeaderMiddle>
        {customHeaderCount === 0 ? (
          <>
            {showMonthYearDropdown ? (
              <>
                <Dropdown
                  disableScrollLock
                  placeholder={
                    monthByYearOptions.find((option) => {
                      return getMonth(monthDate) === option.value.monthIndex
                    })?.label
                  }
                  selected={[
                    String(
                      monthByYearOptions.find((option) => getMonth(monthDate) === option.value.monthIndex)?.value
                        .monthIndex,
                    ),
                  ]}
                  onChange={(selected) => {
                    const selectedMonth = monthByYearOptions.find(
                      (option) => option.value.monthIndex === Number(selected[0].value),
                    )
                    if (selectedMonth) {
                      changeMonth(selectedMonth.value.monthIndex)
                      changeYear(selectedMonth.value.year)
                    }
                  }}
                  height={200}
                  nativeOnMobile
                >
                  {monthByYearOptions.map((option) => {
                    return (
                      <Option key={option.label} value={String(option.value.monthIndex)} label={option.label} native>
                        {option.label}
                      </Option>
                    )
                  })}
                </Dropdown>
              </>
            ) : (
              <>
                {!showMonthYearPicker && !showQuarterYearPicker && (
                  <Dropdown
                    disableScrollLock
                    placeholder={months[getMonth(monthDate)]}
                    selected={[months[getMonth(monthDate)]]}
                    onChange={(selected: Selected) => {
                      changeMonth(months.indexOf(selected[0].value))
                    }}
                    height={200}
                    nativeOnMobile
                  >
                    {months.map((option) => (
                      <Option key={option} native value={option} label={option}>
                        {option}
                      </Option>
                    ))}
                  </Dropdown>
                )}

                <Dropdown
                  disableScrollLock
                  placeholder={getYear(date)}
                  onChange={(selected: Selected) => changeYear(Number(selected[0].value))}
                  height={200}
                  nativeOnMobile
                  selected={[String(getYear(date))]}
                >
                  {dateYears.map((dateYear) => {
                    const year = getYear(dateYear)

                    return (
                      <Option key={year} native value={String(year)} label={String(year)}>
                        {year}
                      </Option>
                    )
                  })}
                </Dropdown>
              </>
            )}
          </>
        ) : (
          <S.datePicker.Label>
            {monthDate.toLocaleString(locale.code, {
              month: 'long',
              year: 'numeric',
            })}
          </S.datePicker.Label>
        )}
      </S.datePicker.HeaderMiddle>

      {(alwaysShowControls ? true : customHeaderCount % 2 !== 0) && !showQuarterYearPicker && (
        <S.datePicker.NextButton type="button" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
          <glyphs.stroke.CaretDown size={18} />
        </S.datePicker.NextButton>
      )}
    </S.datePicker.Header>
  )
}

export const DatePicker = styled(DatePickerComponent)<DatePickerProps>`
  &:global(.datepicker-wrapper) {
    width: fit-content;
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker) {
    border-radius: 4px;
    border: ${() => S.datePicker.BORDER};
    display: flex;
    font-family: ${fonts.PRIMARY_FONT};
    width: fit-content;
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker-time__header) {
    ${S.heading.styles.XS};
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__header) {
    background-color: ${colors.PRIMARY_GREY_000};
    border-bottom: 0px;
  }
  &:global(.datepicker-wrapper)
    :global(.react-datepicker__header--time):not(:global(.react-datepicker__header--time--only)) {
    padding-top: 23px;
    padding-bottom: 19px;
    border-bottom: ${() => S.datePicker.BORDER};
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__close-icon)::after {
    content: '';
    background-color: ${colors.PRIMARY_BLUE_500};
    /* HACK : inline glyph close  */
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" fill-rule="evenodd" clip-rule="evenodd" viewBox="0 0 19 19" width="8"><g><path d="M2.7464 4.1606c-.4122-.4122-.4298-1.063-.0393-1.4535.3905-.3905 1.0413-.373 1.4535.0393L16.103 14.6886c.4122.4123.4298 1.063.0392 1.4535-.3905.3906-1.0412.373-1.4535-.0392L2.7464 4.1606Z" fill="white" /><path d="M4.1606 16.1029c-.4122.4122-1.063.4298-1.4535.0392-.3905-.3905-.373-1.0412.0393-1.4535L14.6886 2.7464c.4123-.4122 1.063-.4298 1.4535-.0393.3906.3905.373 1.0413-.0392 1.4535L4.1606 16.1029Z" fill="white" /></g></svg>');
    background-repeat: no-repeat;
    background-position: center;
    transition: background-color ${() => S.datePicker.TRANSITION};
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__close-icon):hover::after {
    background-color: ${colors.PRIMARY_BLUE_700};
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__day) {
    ${S.copy.styles.M.light};
    background-color: transparent;
    border-radius: 100%;
    border: 1px solid transparent;
    box-sizing: border-box;
    height: 40px;
    line-height: 28px;
    margin: 0px;
    padding: 5px;
    text-align: center;
    transition:
      background-color ${() => S.datePicker.TRANSITION},
      color ${() => S.datePicker.TRANSITION};
    vertical-align: bottom;
    width: 40px;
    @media screen and ${bp.MOBILE_L} {
      margin: 3px;
    }
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__day--highlighted) {
    background-color: transparent;
    color: ${colors.PRIMARY_BLUE_500};
  }

  &:global(.datepicker-wrapper)
    :global(.react-datepicker__day--selected):not(
      &:global(.datepicker-wrapper) :global(.react-datepicker__day--in-range)
    ) {
    background-color: ${colors.PRIMARY_BLUE_500};
    color: ${colors.PRIMARY_GREY_000};
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__day--today) {
    border: ${() => S.datePicker.BORDER};
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__day--excluded),
  &:global(.datepicker-wrapper) :global(.react-datepicker__day--disabled),
  &:global(.datepicker-wrapper) :global(.react-datepicker__day--outside-month) {
    color: ${colors.PRIMARY_GREY_500};
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__day--in-range) {
    border-radius: 0;
    background-color: ${colors.PRIMARY_BLUE_100};
    color: ${colors.PRIMARY_BLUE_500};
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__day--in-range):global(.react-datepicker__day--excluded) {
    background-color: ${colors.PRIMARY_GREY_000};
    color: ${colors.PRIMARY_GREY_500};
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__day--in-selecting-range) {
    color: ${colors.PRIMARY_BLUE_500};
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__day-name) {
    ${S.copy.styles.M.light};
    color: ${colors.TEXT_TERTIARY};
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__day-names) {
    display: flex;
    justify-content: space-around;
    margin-top: 21px;
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__day:hover) {
    background-color: ${colors.PRIMARY_BLUE_100};
    color: ${colors.PRIMARY_BLUE_500};
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__input-container) {
    width: fit-content;
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__input-time-container) {
    position: absolute;
    bottom: -3px;
    display: flex;
    align-items: center;
    width: fit-content;
  }
  &:global(.datepicker-wrapper)
    :global(.react-datepicker__input-time-container)
    :global(.react-datepicker-time__caption) {
    ${S.copy.styles.XS.regular};
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__month-text) {
    ${S.copy.styles.M.light};
    width: 99px;
    height: 28px;
    text-align: center;
    margin-bottom: 15px;
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__month-text):hover {
    background-color: transparent;
    color: ${colors.PRIMARY_BLUE_500};
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__month-text--selected),
  &:global(.datepicker-wrapper) :global(.react-datepicker__month-text--keyboard-selected) {
    color: ${colors.PRIMARY_BLUE_500};
    background-color: transparent;
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__month-text--selected):hover,
  &:global(.datepicker-wrapper) :global(.react-datepicker__month-text--keyboard-selected):hover {
    background-color: transparent;
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__month--in-range) {
    color: ${colors.PRIMARY_BLUE_500};
    background-color: transparent;
    /* &--in-selecting-range don't exist for month */
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__month--selected) {
    color: ${colors.PRIMARY_BLUE_500};
    background-color: transparent;
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__month--selected):hover {
    background-color: transparent;
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__quarter-text) {
    ${S.copy.styles.M.light};
    width: 99px;
    height: 28px;
    text-align: center;
    margin-bottom: 15px;
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__quarter-text):hover {
    background-color: transparent;
    color: ${colors.PRIMARY_BLUE_500};
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__quarter--selected),
  &:global(.datepicker-wrapper) :global(.react-datepicker__quarter--in-range) {
    color: ${colors.PRIMARY_BLUE_500};
    background-color: transparent;
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__quarter--selected):hover,
  &:global(.datepicker-wrapper) :global(.react-datepicker__quarter--in-range):hover {
    background-color: transparent;
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__time-container),
  &:global(.datepicker-wrapper)
    :global(.react-datepicker__time-container)
    :global(.react-datepicker__time)
    :global(.react-datepicker__time-box) {
    width: 95px;
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__time-container) {
    border-left-color: ${() => S.datePicker.BORDER_COLOR};
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__time-list-item) {
    ${S.copy.styles.M.light};
    transition:
      background-color ${() => S.datePicker.TRANSITION},
      color ${() => S.datePicker.TRANSITION};
    line-height: 28px;
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__time-list-item--selected) {
    background-color: ${colors.PRIMARY_BLUE_500} !important;
    font-weight: normal !important;
  }
  &:global(.datepicker-wrapper)
    :global(.react-datepicker__time-list-item):not(
      &:global(.datepicker-wrapper) :global(.react-datepicker__time-list-item--selected)
    ):hover {
    background-color: ${colors.PRIMARY_BLUE_100} !important;
    color: ${colors.PRIMARY_BLUE_500};
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__triangle) {
    stroke: ${() => S.datePicker.BORDER_COLOR};
    fill: ${colors.PRIMARY_GREY_000};
    color: ${colors.PRIMARY_GREY_000};
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__week-number) {
    ${S.copy.styles.S.regular};
    height: 40px;
    line-height: 34px;
    width: 40px;
    color: ${colors.PRIMARY_SAND_500};
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__week-number--selected) {
    transition:
      background-color ${S.datePicker.TRANSITION},
      color ${S.datePicker.TRANSITION};
    ${S.copy.styles.M.light};
    background-color: ${colors.PRIMARY_BLUE_500};
    border-radius: 100%;
    box-sizing: border-box;
    color: ${colors.PRIMARY_GREY_000};
    line-height: 28px;
    padding: 5px;
    vertical-align: bottom;
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__year-text) {
    ${S.copy.styles.M.light};
    width: 85px;
    height: 28px;
    text-align: center;
    margin-bottom: 15px;
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__year-text):hover {
    color: ${colors.PRIMARY_BLUE_500};
    background-color: transparent;
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__year-text--selected),
  &:global(.datepicker-wrapper) :global(.react-datepicker__year-text--keyboard-selected) {
    color: ${colors.PRIMARY_BLUE_500};
    background-color: transparent;
  }
  &:global(.datepicker-wrapper) :global(.react-datepicker__year-text--selected):hover,
  &:global(.datepicker-wrapper) :global(.react-datepicker__year-text--keyboard-selected):hover {
    background-color: transparent;
  }

  ${({ monthsShown }) => {
    if (monthsShown ? monthsShown >= 2 : false) {
      return css`
        max-width: 300px;
        flex-wrap: nowrap;
        overflow-x: auto;
        overflow-y: hidden;

        @media screen and ${bp.MOBILE_M} {
          max-width: 370px;
        }

        @media screen and ${bp.TABLET} {
          max-width: 671px;
        }

        :global(.datepicker-wrapper) :global(.react-datepicker__month-container) {
          border-right: ${() => S.datePicker.BORDER};

          &:last-child {
            border-right: none;
          }
        }

        :global(.datepicker-wrapper) :global(.react-datepicker__time-container) {
          border-left: 0px;
        }
      `
    }
  }};

  ${({ showTimeInput }) => {
    if (showTimeInput) {
      return css`
        padding-bottom: 70px;
      `
    }
  }};
`
DatePicker.displayName = 'DatePicker'

function verifyPropsCompatibility({ ...props }: DatePickerProps) {
  const {
    monthsShown,
    showMonthYearPicker,
    showQuarterYearPicker,
    showTimeSelectOnly,
    showTwoColumnMonthYearPicker,
    showYearPicker,
    selectsRange,
    showPreviousMonths,
    showTimeSelect,
    showWeekNumbers,
    inline,
    isClearable,
    withPortal,
    showTimeInput,
  } = props

  // We have two level of incompatibility
  // error throw errors
  // info just console log the incompatibility
  const errors = []
  const infos = []

  // ERRORS

  if (
    monthsShown &&
    monthsShown > 1 &&
    (showMonthYearPicker ||
      showQuarterYearPicker ||
      showTimeSelectOnly ||
      showTwoColumnMonthYearPicker ||
      showYearPicker)
  ) {
    errors.push(
      "monthsShown can't be set with showMonthYearPicker || showQuarterYearPicker || showTimeSelectOnly || showTwoColumnMonthYearPicker || showYearPicker",
    )
  }

  if (
    selectsRange &&
    (showPreviousMonths || showTimeSelect || showTimeSelectOnly || showTwoColumnMonthYearPicker || showYearPicker)
  ) {
    errors.push(
      "selectsRange can't be set with showPreviousMonths || showTimeSelect || showTimeSelectOnly || showTwoColumnMonthYearPicker || showYearPicker",
    )
  }

  if (showMonthYearPicker && showWeekNumbers) {
    errors.push("showMonthYearPicker can't be set with showWeekNumbers")
  }

  if (inline && (isClearable || withPortal)) {
    errors.push("inline can't be set with isClearable || withPortal")
  }

  if (showQuarterYearPicker && showWeekNumbers) {
    errors.push("showQuarterYearPicker can't be set with showWeekNumbers")
  }

  if (showTimeSelectOnly && showWeekNumbers) {
    errors.push("showTimeSelectOnly can't be set with  showWeekNumbers")
  }

  if (showTwoColumnMonthYearPicker && showWeekNumbers) {
    errors.push("showTwoColumnMonthYearPicker can't be set with showWeekNumbers")
  }

  if (showWeekNumbers && showYearPicker) {
    errors.push("showWeekNumbers can't be set with showYearPicker")
  }

  // INFOS

  if (showMonthYearPicker && (showPreviousMonths || showQuarterYearPicker || showTimeSelectOnly || showYearPicker)) {
    infos.push(
      "showMonthYearPicker can't be set with showPreviousMonths || showQuarterYearPicker || showTimeSelectOnly || showYearPicker",
    )
  }

  if (showPreviousMonths && (showQuarterYearPicker || showTimeSelectOnly || showYearPicker)) {
    infos.push("showPreviousMonths can't be set with showQuarterYearPicker ||  showTimeSelectOnly ||  showYearPicker")
  }

  if (showQuarterYearPicker && (showTimeSelectOnly || showTwoColumnMonthYearPicker || showYearPicker)) {
    infos.push(
      "showQuarterYearPicker can't be set with showTimeSelectOnly || showTwoColumnMonthYearPicker || showYearPicker",
    )
  }

  if (showTimeInput && showTimeSelectOnly) {
    infos.push("showTimeInput can't be set with showTimeSelectOnly")
  }

  if (showTimeSelect && showYearPicker) {
    infos.push("showTimeSelect can't be set with showYearPicker")
  }

  if (showTimeSelectOnly && (showTwoColumnMonthYearPicker || showYearPicker)) {
    infos.push("showTimeSelectOnly can't be set with showTwoColumnMonthYearPicker || showYearPicker")
  }

  if (showTwoColumnMonthYearPicker && showYearPicker) {
    infos.push("showTwoColumnMonthYearPicker can't be set with showYearPicker")
  }

  if (errors.length > 0) {
    throw errors.join(' - ')
  }

  if (infos.length > 0) {
    console.warn(infos.join(' - '))
  }
}
